import type { SupabaseClient } from '../client'
import { SupabaseError } from '../errors'

/**
 * O que o cliente já viu.
 *
 * Serve só ao ponto de novidade da lista: um chamado pode ter resposta fresca
 * da equipe sem exigir gesto nenhum dele, e sem esta marca nada o chamaria de
 * volta à tela.
 *
 * A leitura é direta na tabela (a RLS já limita o cliente às próprias linhas) e
 * a escrita passa por RPC, para o portal nunca precisar mandar o próprio id:
 * quem é o dono da marca é o banco que decide.
 */

export type VistoRow = { chamado_ref: string; visto_em: string }

/** Mapa de id do item na tela para quando ele foi aberto pela última vez. */
export async function listChamadosVistos(client: SupabaseClient): Promise<Record<string, string>> {
  const { data, error } = await client
    .schema('portal')
    .from('chamado_visto')
    .select('chamado_ref, visto_em')

  if (error) throw new SupabaseError(error.message, error)

  const mapa: Record<string, string> = {}
  for (const linha of (data ?? []) as VistoRow[]) {
    mapa[linha.chamado_ref] = linha.visto_em
  }
  return mapa
}

// a coluna e as RPCs do ADR-100 nasceram depois da última regeração dos types
// biome-ignore lint/suspicious/noExplicitAny: coluna e RPCs fora dos types gerados
const portalSemTipos = (client: SupabaseClient) => client.schema('portal') as any

/**
 * ADR-100 · mapa de id do item na tela para quando ele foi arquivado.
 *
 * Só as marcas com arquivo. Se o item está arquivado AGORA é conta da tela
 * (`lib/arquivo.js`): novidade depois do arquivo tira do arquivo.
 */
export async function listChamadosArquivados(
  client: SupabaseClient,
): Promise<Record<string, string>> {
  const { data, error } = await portalSemTipos(client)
    .from('chamado_visto')
    .select('chamado_ref, arquivado_em')
    .not('arquivado_em', 'is', null)

  if (error) throw new SupabaseError(error.message, error)

  const mapa: Record<string, string> = {}
  for (const linha of (data ?? []) as { chamado_ref: string; arquivado_em: string }[]) {
    mapa[linha.chamado_ref] = linha.arquivado_em
  }
  return mapa
}

/** Tira o chamado da lista de quem pediu. Devolve a hora do arquivo. */
export async function arquivarChamado(client: SupabaseClient, ref: string): Promise<string> {
  const { data, error } = await portalSemTipos(client).rpc('arquivar_chamado', { p_ref: ref })
  if (error) throw new SupabaseError(error.message, error)
  return data as string
}

/** Devolve o chamado à lista. */
export async function desarquivarChamado(client: SupabaseClient, ref: string): Promise<void> {
  const { error } = await portalSemTipos(client).rpc('desarquivar_chamado', { p_ref: ref })
  if (error) throw new SupabaseError(error.message, error)
}

/**
 * Marca o chamado como visto agora.
 *
 * Devolve `null` para quem não tem client_user, que é a equipe entrando pelo
 * ERP. O preview tem conta de cliente própria e grava na conta dele, então a
 * equipe olhando o portal nunca apaga a novidade do cliente de verdade.
 */
export async function marcarChamadoVisto(
  client: SupabaseClient,
  ref: string,
): Promise<string | null> {
  const { data, error } = await client.schema('portal').rpc('marcar_chamado_visto', { p_ref: ref })

  if (error) throw new SupabaseError(error.message, error)
  return (data as string | null) ?? null
}
